import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageContainer, PageHeader, PrimaryButton, SecondaryButton, EmptyState, Field, inputCls } from "@/components/ui-kit";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { UserPlus, Trash2, RefreshCw, X, ShieldAlert, History } from "lucide-react";
import { UserRoleHistory } from "@/components/UserRoleHistory";

function NewUserModal({ onClose, onCreated }) {
  const [data, setData] = useState({ email: "", name: "", role: "operator", password: "AA123!", must_change_password: false });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/users", data);
      toast.success("Usuario creado");
      onCreated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-lg border-t-4 border-[#FF5A00]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
          <div className="font-display font-bold text-xl">Nuevo usuario</div>
          <button type="button" onClick={onClose} className="hover:bg-neutral-100 p-2"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 grid grid-cols-1 gap-4">
          <Field label="Email *">
            <input type="email" required data-testid="user-email-input" value={data.email} onChange={(e) => setData({ ...data, email: e.target.value })} className={inputCls} />
          </Field>
          <Field label="Nombre *">
            <input required data-testid="user-name-input" value={data.name} onChange={(e) => setData({ ...data, name: e.target.value })} className={inputCls} />
          </Field>
          <Field label="Rol">
            <select value={data.role} onChange={(e) => setData({ ...data, role: e.target.value })} className={inputCls} data-testid="user-role-select">
              <option value="operator">Operador (CRM)</option>
              <option value="technician">Técnico (app de campo)</option>
              <option value="admin">Administrador</option>
            </select>
          </Field>
          <Field label="Contraseña temporal (mín. 6)">
            <input data-testid="user-password-input" value={data.password} onChange={(e) => setData({ ...data, password: e.target.value })} className={inputCls} />
          </Field>
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={data.must_change_password} onChange={(e) => setData({ ...data, must_change_password: e.target.checked })} className="w-4 h-4" />
            Forzar cambio de contraseña en primer ingreso
          </label>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-neutral-200 bg-neutral-50">
          <SecondaryButton type="button" onClick={onClose}>Cancelar</SecondaryButton>
          <PrimaryButton type="submit" disabled={saving} data-testid="user-form-submit">{saving ? "Creando…" : "Crear usuario"}</PrimaryButton>
        </div>
      </form>
    </div>
  );
}

export default function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [roleFilter, setRoleFilter] = useState("all");
  const [savingRole, setSavingRole] = useState(null);
  const [historyUser, setHistoryUser] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      if (e.response?.status === 403) setForbidden(true);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const reset = async (u) => {
    if (!window.confirm(`Restablecer contraseña de ${u.email} a "AA123!"?`)) return;
    await api.post(`/users/${u.id}/reset-password`);
    toast.success("Contraseña restablecida a AA123!");
    load();
  };

  const remove = async (u) => {
    if (!window.confirm(`Eliminar usuario ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Usuario eliminado");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const roleLabel = { admin: "Administrador", operator: "Operador", technician: "Técnico" };

  const changeRole = async (u, newRole) => {
    if (newRole === u.role) return;
    if (!window.confirm(`Cambiar rol de ${u.name} de "${roleLabel[u.role] || u.role}" a "${roleLabel[newRole] || newRole}"?`)) return;
    setSavingRole(u.id);
    try {
      await api.patch(`/users/${u.id}`, { role: newRole, expected_role: u.role });
      toast.success(`Rol actualizado a ${roleLabel[newRole] || newRole}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      if (e.response?.status === 409) await load();
    } finally {
      setSavingRole(null);
    }
  };

  if (forbidden) {
    return (
      <PageContainer>
        <PageHeader kicker="Acceso restringido" title="Usuarios" />
        <div className="bg-[#FFC107]/10 border border-[#FFC107]/30 p-6 flex items-start gap-3">
          <ShieldAlert className="w-5 h-5 text-[#B28C00] mt-0.5" />
          <div className="text-sm">Solo administradores pueden gestionar usuarios. Tu rol actual es <b>{me?.role}</b>.</div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        kicker={`${users.length} cuentas · ${users.filter(u => u.role === 'technician').length} técnicos`}
        title="Usuarios y técnicos"
        subtitle="Equipo con acceso al panel y a la app de terreno. La contraseña inicial AA123! debe cambiarse en el primer ingreso."
        actions={
          <PrimaryButton data-testid="users-new-button" onClick={() => setShow(true)}>
            <UserPlus className="w-3.5 h-3.5 inline mr-2 -mt-0.5" /> Nuevo usuario
          </PrimaryButton>
        }
      />

      <div className="flex flex-wrap gap-2 mb-6">
        {[
          { value: "all", label: `Todos (${users.length})` },
          { value: "admin", label: `Admins (${users.filter(u => u.role === 'admin').length})` },
          { value: "operator", label: `Operadores (${users.filter(u => u.role === 'operator').length})` },
          { value: "technician", label: `Técnicos (${users.filter(u => u.role === 'technician').length})` },
        ].map((f) => (
          <button
            key={f.value}
            data-testid={`users-filter-${f.value}`}
            onClick={() => setRoleFilter(f.value)}
            className={`px-3 py-1.5 font-mono-kpi text-[10px] uppercase tracking-[0.15em] border ${roleFilter === f.value ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-neutral-300"}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="font-mono-kpi text-xs text-neutral-500 uppercase tracking-[0.2em]">Cargando…</div>
      ) : users.length === 0 ? (
        <EmptyState title="Sin usuarios" />
      ) : (
        <div className="bg-white border border-neutral-200">
          <div className="hidden xl:grid xl:grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)_150px_100px_160px] gap-3 px-5 py-3 bg-neutral-50 border-b border-neutral-200 font-mono-kpi text-[10px] uppercase tracking-[0.15em] text-neutral-500">
            <div>Nombre</div><div>Email</div><div>Rol</div><div>Estado</div><div className="text-right">Acciones</div>
          </div>
          {users.filter(u => roleFilter === "all" || u.role === roleFilter).map((u) => (
            <div key={u.id} data-testid={`user-row-${u.id}`} className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)_150px_100px_160px] gap-3 px-5 py-4 border-b border-neutral-100 items-center text-sm">
              <div data-testid={`user-name-${u.id}`} className="font-medium break-words min-w-0">{u.name}{u.id === me?.id && <span className="ml-2 font-mono-kpi text-[9px] text-[#FF5A00]">(tú)</span>}</div>
              <div data-testid={`user-email-${u.id}`} className="text-neutral-600 break-all min-w-0">{u.email}</div>
              <div>
                <select
                  data-testid={`user-role-select-${u.id}`}
                  aria-label={`Rol de ${u.name}`}
                  value={u.role}
                  onChange={(e) => changeRole(u, e.target.value)}
                  disabled={savingRole === u.id || u.id === me?.id}
                  title={u.id === me?.id ? "No puedes cambiar tu propio rol" : ""}
                  className={`w-full font-mono-kpi text-[10px] uppercase tracking-[0.1em] px-2 py-1 border ${u.id === me?.id ? "cursor-not-allowed" : "cursor-pointer"} ${
                    u.role === "admin"
                      ? "bg-[#FF5A00]/10 text-[#C2410C] border-[#FF5A00]/40"
                      : u.role === "technician"
                        ? "bg-[#2962FF]/10 text-[#1E40AF] border-[#2962FF]/30"
                        : "bg-neutral-100 text-neutral-700 border-neutral-300"
                  } disabled:opacity-60`}
                >
                  <option value="admin">Admin</option>
                  <option value="operator">Operador</option>
                  <option value="technician">Técnico</option>
                </select>
                <button data-testid={`user-role-history-${u.id}`} type="button" className="mt-2 text-xs text-neutral-600 hover:text-[#C2410C] inline-flex items-center gap-1 transition-colors" onClick={() => setHistoryUser(u)}><History size={13} />Historial de rol</button>
              </div>
              <div>
                {u.must_change_password ? (
                  <span data-testid={`user-password-state-${u.id}`} className="font-mono-kpi text-[10px] uppercase px-2 py-0.5 bg-[#FFC107]/10 text-[#B28C00] border border-[#FFC107]/30">Debe cambiar PW</span>
                ) : (
                  <span data-testid={`user-password-state-${u.id}`} className="font-mono-kpi text-[10px] uppercase text-neutral-500">OK</span>
                )}
              </div>
              <div className="flex flex-wrap items-center justify-start xl:justify-end gap-1">
                <button data-testid={`user-reset-${u.id}`} onClick={() => reset(u)} className="font-mono-kpi text-[10px] uppercase tracking-[0.15em] text-neutral-600 hover:text-[#FF5A00] hover:bg-neutral-50 px-2 py-1 border border-neutral-200">
                  <RefreshCw className="w-3 h-3 inline -mt-0.5 mr-0.5" />Reset
                </button>
                {u.id !== me?.id && (
                  <button data-testid={`user-delete-${u.id}`} onClick={() => remove(u)} className="font-mono-kpi text-[10px] uppercase tracking-[0.15em] text-[#D50000] hover:bg-[#D50000]/5 px-2 py-1 border border-[#D50000]/30">
                    <Trash2 className="w-3 h-3 inline -mt-0.5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {show && <NewUserModal onClose={() => setShow(false)} onCreated={() => { setShow(false); load(); }} />}
      <UserRoleHistory user={historyUser} onClose={() => setHistoryUser(null)} />
    </PageContainer>
  );
}
